<script setup lang="ts">
import type { CustomerPortraitDownloadItem } from './customerPortraitDownloadTypes';

interface Props {
  index: number;
  portrait: CustomerPortraitDownloadItem;
}

interface Emits {
  download: [portrait: CustomerPortraitDownloadItem];
  retry: [index: number];
  share: [portrait: CustomerPortraitDownloadItem, index: number];
}

defineProps<Props>();

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const emit = defineEmits<Emits>();
</script>

<template>
  <div
    v-if="portrait.isError"
    class="CustomerPortraitsDownloadCode__error"
    data-testid="customer-download-code-error"
  >
    {{ $t('CustomerPortraitsDownloadCode.error') }}
    <button class="kiosk-button" type="button" @click.stop="emit('retry', index)">
      {{ $t('CustomerPortraitsDownloadCode.buttonRetry') }}
    </button>
  </div>

  <div
    v-else-if="!portrait.isPreloaded"
    class="CustomerPortraitsDownloadCode__placeholder"
  >
    <div class="CustomerPortraitsDownloadCode__placeholder-label">
      {{ $t('CustomerPortraitsDownloadCode.placeholder') }}
    </div>
  </div>

  <div
    v-else
    class="CustomerPortraitsDownloadCode__buttons"
    :class="{
      'CustomerPortraitsDownloadCode__buttons--landscape':
        portrait.aspectRatio && portrait.aspectRatio > 1,
    }"
  >
    <button
      class="CustomerPortraitsDownloadCode__buttons-button CustomerPortraitsDownloadCode__buttons-button--primary kiosk-button"
      data-testid="customer-download-download-btn"
      :class="{
        'CustomerPortraitsDownloadCode__buttons-button--loading':
          portrait.isDownloading,
      }"
      type="button"
      :aria-busy="portrait.isDownloading"
      :disabled="portrait.isDownloading"
      @click.stop="emit('download', portrait)"
    >
      {{
        $t(
          portrait.downloadError
            ? 'CustomerPortraitsDownloadCode.actions.retryDownload'
            : 'CustomerPortraitsDownloadCode.buttonDownload'
        )
      }}
    </button>
    <button
      class="CustomerPortraitsDownloadCode__buttons-button CustomerPortraitsDownloadCode__buttons-button--secondary kiosk-button"
      data-testid="customer-download-share-btn"
      type="button"
      @click.stop="emit('share', portrait, index)"
    >
      {{ $t('CustomerPortraitsDownloadCode.buttonSendOrShare') }}
    </button>
    <p
      v-if="portrait.downloadError || portrait.shareStatus"
      class="CustomerPortraitsDownloadCode__action-status"
      :role="portrait.shareStatus === 'error' || portrait.downloadError ? 'alert' : 'status'"
      aria-live="polite"
    >
      {{
        $t(
          portrait.downloadError
            ? 'CustomerPortraitsDownloadCode.actions.downloadFailed'
            : portrait.shareStatus === 'copied'
              ? 'CustomerPortraitsDownloadCode.actions.linkCopied'
              : 'CustomerPortraitsDownloadCode.actions.shareFailed'
        )
      }}
    </p>
  </div>
</template>

<style lang="scss" scoped>
@keyframes CustomerPortraitsDownloadCode__placeholder {
  0% {
    background-position: 0 -100%;
  }

  100% {
    background-position: 0 200%;
  }
}

.CustomerPortraitsDownloadCode__placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20%;
  position: absolute;
  inset: 0;
  text-align: center;
  border-radius: var(--ImageGallery--item-radius);
  font-size: calc(var(--ImageGallery--text-size) * 1.13);
  text-transform: uppercase;
  background: rgba(var(--Kiosk--tint-color-rgb), 0.15);
  box-shadow: inset 0 0 1rem 0.3rem rgba(var(--Kiosk--tint-color-rgb), 0.2);
  color: var(--Kiosk--tint-inverse-color);

  &:before {
    content: '';
    position: absolute;
    inset: 0;
    background: {
      image: linear-gradient(
        0deg,
        transparent,
        rgba(var(--Kiosk--tint-color-rgb), 0.35),
        transparent
      );
      size: 100% 200%;
      repeat: no-repeat;
    };
    border-radius: var(--ImageGallery--item-radius);
    animation: CustomerPortraitsDownloadCode__placeholder 3s linear infinite;
  }
}

.CustomerPortraitsDownloadCode__placeholder-label {
  position: relative;
  z-index: 1;
}

.CustomerPortraitsDownloadCode__buttons {
  --CustomerPortraitsDownloadCode__buttons-gap: calc(
    var(--ImageGallery--text-size) * 0.75
  );

  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  position: relative;
  isolation: isolate;
  width: 100%;
  border-radius: 0 0 var(--ImageGallery--item-radius)
    var(--ImageGallery--item-radius);
  padding:
    calc(var(--CustomerPortraitsDownloadCode__buttons-gap) * 2.5)
    var(--CustomerPortraitsDownloadCode__buttons-gap)
    var(--CustomerPortraitsDownloadCode__buttons-gap);

  &::before {
    content: '';
    position: absolute;
    z-index: 0;
    inset: 0;
    border-radius: inherit;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      rgb(96 96 96 / 12%) 30%,
      rgb(72 72 72 / 78%) 100%
    );
    pointer-events: none;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  &--landscape {
    justify-content: center;
    gap: var(--CustomerPortraitsDownloadCode__buttons-gap);

    .CustomerPortraitsDownloadCode__buttons-button {
      width: min(
        18em,
        calc(50% - (var(--CustomerPortraitsDownloadCode__buttons-gap) / 2))
      );
      height: 4em;
    }
  }
}

.CustomerPortraitsDownloadCode__buttons-button {
  --CustomerPortraitsDownloadCode__buttons-button--font-size: calc(
    var(--ImageGallery--text-size) *
      var(--customer-download-action-font-scale, 0.75)
  );

  width: calc(50% - (var(--CustomerPortraitsDownloadCode__buttons-gap) / 2));
  font-size: var(--CustomerPortraitsDownloadCode__buttons-button--font-size);
  text-decoration: none;
  box-shadow: 0 0.18rem 0.65rem rgba(#000000, 0.42);

  &--secondary {
    --kiosk-button-color: var(--Kiosk--button_background_color);
    --kiosk-button-background: var(--Kiosk--button_font_color);
  }

  &--loading {
    pointer-events: none;
    filter: grayscale(1);
    opacity: 0.8;

    &:after {
      content: '…';
    }
  }
}

.CustomerPortraitsDownloadCode__action-status {
  flex: 1 0 100%;
  margin-top: var(--CustomerPortraitsDownloadCode__buttons-gap);
  color: #ffffff;
  font-size: calc(var(--ImageGallery--text-size) * 0.62);
  line-height: 1.3;
  text-align: center;
  text-shadow: 0 0.1rem 0.35rem rgba(#000000, 0.9);

  &[role='alert'] {
    padding: 0.55em 0.75em;
    border: 1px solid #fca5a5;
    border-radius: 0.45em;
    color: #991b1b;
    background: #fef2f2;
    text-shadow: none;
  }
}

.CustomerPortraitsDownloadCode__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20%;
  position: absolute;
  inset: 0;
  font-size: var(--ImageGallery--text-size);
  text-align: center;
  text-transform: uppercase;
  border-radius: var(--ImageGallery--item-radius);
  background: rgba(var(--Kiosk--tint-color-rgb), 0.15);
  box-shadow: inset 0 0 1rem 0.3rem rgba(var(--Kiosk--tint-color-rgb), 0.2);
  color: var(--Kiosk--tint-inverse-color);

  .kiosk-button {
    margin-top: var(--ImageGallery--text-size);
    font-size: calc(var(--ImageGallery--text-size) * 0.75);
  }
}

@media (max-width: 40rem) {
  .CustomerPortraitsDownloadCode__buttons {
    --CustomerPortraitsDownloadCode__buttons-gap: calc(
      var(--ImageGallery--text-size) * 0.55
    );

    gap: var(--CustomerPortraitsDownloadCode__buttons-gap);
    padding:
      max(var(--CustomerPortraitsDownloadCode__buttons-gap), 6%)
      var(--CustomerPortraitsDownloadCode__buttons-gap)
      var(--CustomerPortraitsDownloadCode__buttons-gap);
  }

  .CustomerPortraitsDownloadCode__buttons-button {
    --kiosk-button-padding: 0
      calc(var(--CustomerPortraitsDownloadCode__buttons-gap) * 0.8);
  }

  .CustomerPortraitsDownloadCode__action-status {
    margin-top: 0;
  }
}
</style>
